import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DASHBOARD } from "@/constants/testIds";
import api from "@/lib/api";
import {
  Users as UsersIcon, FileSignature, Clock, Activity,
  CheckCircle2, AlertCircle, ShieldAlert, RefreshCw,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

function StatCard({ label, value, sub, icon: Icon, testId, tone = "orange" }) {
  const tones = {
    orange: "bg-orange-50 text-orange-700 border-orange-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <Card className="p-5 border-slate-200 shadow-sm" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
            {label}
          </div>
          <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md border flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const chartData = stats?.sync_history?.length
    ? stats.sync_history
    : [{ date: "Sin datos", processed: 0, succeeded: 0 }];

  return (
    <div className="space-y-8" data-testid={DASHBOARD.root}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">
            Panel principal
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Estado de sincronización, plantillas y actividad reciente.
          </p>
        </div>
        <Button
          variant="outline" onClick={load}
          className="border-slate-200 text-slate-700 hover:bg-slate-50"
          data-testid="dashboard-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Actualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Usuarios totales" value={loading ? "—" : stats?.total_users ?? 0}
          sub={`${stats?.active_users ?? 0} activos`}
          icon={UsersIcon} testId={DASHBOARD.cardTotalUsers}
        />
        <StatCard
          label="Firmas activas" value={loading ? "—" : stats?.active_templates ?? 0}
          sub={`${stats?.total_templates ?? 0} plantillas totales`}
          icon={FileSignature} testId={DASHBOARD.cardActiveSignatures} tone="green"
        />
        <StatCard
          label="Firmas pendientes" value={loading ? "—" : stats?.pending_users ?? 0}
          sub="Usuarios sin plantilla asignada"
          icon={AlertCircle} testId={DASHBOARD.cardPendingUsers} tone="amber"
        />
        <StatCard
          label="Última sincronización"
          value={loading ? "—" : stats?.last_sync_at ? new Date(stats.last_sync_at).toLocaleDateString("es") : "Nunca"}
          sub={
            stats?.last_sync_status
              ? (stats.last_sync_status.length > 40
                  ? stats.last_sync_status.slice(0, 40) + "…"
                  : stats.last_sync_status)
              : "Sin ejecuciones"
          }
          icon={Clock} testId={DASHBOARD.cardLastSync} tone="slate"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
                Historial de publicaciones
              </div>
              <h3 className="text-base font-semibold tracking-tight text-slate-900 mt-1">
                Firmas procesadas
              </h3>
            </div>
            <Activity className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
          </div>
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={256}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip
                  contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12 }}
                  cursor={{ fill: "#fff7ed" }}
                />
                <Bar dataKey="succeeded" fill="#ea580c" radius={[4, 4, 0, 0]} name="Exitosas" />
                <Bar dataKey="processed" fill="#cbd5e1" radius={[4, 4, 0, 0]} name="Procesadas" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <div className="mb-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
              Estado del sistema
            </div>
            <h3 className="text-base font-semibold tracking-tight text-slate-900 mt-1">
              Microsoft Graph
            </h3>
          </div>
          <div className="space-y-3" data-testid={DASHBOARD.graphStatus}>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Conexión Graph</span>
              {stats?.graph_configured ? (
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" strokeWidth={2} /> Configurado
                </Badge>
              ) : (
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200">
                  <ShieldAlert className="w-3 h-3 mr-1" strokeWidth={2} /> Modo Demo
                </Badge>
              )}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Método de despliegue</span>
              <span className="text-xs text-slate-500 font-mono">Exchange Transport</span>
            </div>
            <div className="pt-3 border-t border-slate-200">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Auditoría reciente
              </div>
              <div className="space-y-2 max-h-40 overflow-auto">
                {(stats?.recent_audit || []).slice(0, 5).map((a) => (
                  <div key={a.id || `${a.created_at}-${a.action}`} className="text-xs">
                    <div className="text-slate-800 font-medium">{a.action}</div>
                    <div className="text-slate-500">
                      {a.admin_user} · {new Date(a.created_at).toLocaleString("es")}
                    </div>
                  </div>
                ))}
                {(!stats?.recent_audit || stats.recent_audit.length === 0) && (
                  <div className="text-xs text-slate-400">Sin actividad reciente.</div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
